/**
 * Application configuration
 */
import type {ProjectConfig} from '/#/config';

import {PROJ_CFG_KEY} from '@/enums/cacheEnum';
import projectSetting from '@/settings/projectSetting';

import {
  updateHeaderBgColor,
  updateSidebarBgColor,
  updatePageTheme
} from '@/logics/theme/updateBackground';
import {updateColorWeak} from '@/logics/theme/updateColorWeak';
import {updateGrayMode} from '@/logics/theme/updateGrayMode';
import {updateDarkTheme} from '@/logics/theme/dark';
import {changeTheme} from '@/logics/theme';

import {useAppStore} from '@/store/modules/app';
import {useLocaleStore} from '@/store/modules/locale';
import {StorageUtils} from 'xmgoing-utils';

import {getCommonStoragePrefix, getStorageShortName} from '@/utils/env';

import {primaryColor} from '../../build/config/themeConfig';
import {Persistent} from '@/utils/cache/persistent';
import {deepMerge} from '@/utils';
import {ThemeEnum} from '@/enums/appEnum';
import {setCssVar} from "@/logics/theme/util";
import {findMaxRGB, foramgeRGB, getShallowRGB} from "@/utils/colorUtils";
import { getFrameColorPreset } from '@/settings/frameColorPreset';




const PRIMARY_COLOR = '--primary-color';
export const BASE_BORDER_RADUIS = '--baseBorderRaduis';
export const BASE_FONT_SIZE = '--baseFontSize';
export const BASE_SPACE = '--baseSpace';
const ANT_PRIMARY_COLOR = '--ant-primary-color';
const ANT_PRIMARY_COLOR_Hover = '--ant-primary-color-hover';
const ANT_PRIMARY_COLOR_Active='--ant-primary-color-active';

// Initial project configuration
export function initAppConfigStore() {
  const localeStore = useLocaleStore();
  const appStore = useAppStore();
  let projCfg: ProjectConfig = Persistent.getLocal(PROJ_CFG_KEY) as ProjectConfig;
  if(projCfg==null||projCfg===''){
    return;
  }
  const {baseBorderRaduis,baseFontSize,baseSpace}=projCfg;
  projCfg = deepMerge(projectSetting, projCfg || {});
  const darkMode = appStore.getDarkMode;
  const {
    colorWeak,
    grayMode,
    themeColor,
    pageBgColor,
    headerSetting: {bgColor: headerBgColor} = {},
    menuSetting: {bgColor} = {},
  } = projCfg;
  try {
    if (themeColor && themeColor !== primaryColor) {
      const colorRGB = foramgeRGB(themeColor);
      var arr = colorRGB.split(",");
      let maxColorObject = findMaxRGB(parseInt(arr[0]), parseInt(arr[1]), parseInt(arr[2]));
      //生成相近色
      let shallowColorArray:any = getShallowRGB(maxColorObject, parseInt(arr[0]), parseInt(arr[1]), parseInt(arr[2]),0)

      changeTheme(themeColor);
      //基础圆角度
      setCssVar(BASE_BORDER_RADUIS, (baseBorderRaduis||2)+'px');
      //基础字体大小
      setCssVar(BASE_FONT_SIZE, (baseFontSize||14)+'px');
      //基础间距
      setCssVar(BASE_SPACE, (baseSpace||12)+'px');

      setCssVar(PRIMARY_COLOR, themeColor);
      setCssVar(ANT_PRIMARY_COLOR, themeColor);
      setCssVar(ANT_PRIMARY_COLOR_Hover, `rgb(${shallowColorArray[1].toString()})`);
      setCssVar(ANT_PRIMARY_COLOR_Active, `rgb(${shallowColorArray[1].toString()})`);
      // let shallowColorArrayTwo = getShallowRGB(maxColorObject, parseInt(arr[0]), parseInt(arr[1]), parseInt(arr[2]),1);
      // let shallowColorArrayThree = getShallowRGB(maxColorObject, parseInt(arr[0]), parseInt(arr[1]), parseInt(arr[2]),3);

      for(let num=1;num<=7;num++){
        if(num===1){
          setCssVar('--ant-primary-'+num, `rgb(${parseInt(arr[0])} ${parseInt(arr[1])} ${parseInt(arr[2])} / 30%)`);
        }
        if(num===2){
          setCssVar('--ant-primary-'+num, `rgb(${parseInt(arr[0])} ${parseInt(arr[1])} ${parseInt(arr[2])} / 50%)`);
        }
        if(num===3){
          setCssVar('--ant-primary-'+num, `rgb(${parseInt(arr[0])} ${parseInt(arr[1])} ${parseInt(arr[2])} / 60%)`);
        }
        if(num===4){
          setCssVar('--ant-primary-'+num, `rgb(${parseInt(arr[0])} ${parseInt(arr[1])} ${parseInt(arr[2])} / 65%)`);
        }
        if(num===5){
          setCssVar('--ant-primary-'+num, `rgb(${parseInt(arr[0])} ${parseInt(arr[1])} ${parseInt(arr[2])} / 70%)`);
        }
        if(num===6){
          setCssVar('--ant-primary-'+num,`rgb(${parseInt(arr[0])} ${parseInt(arr[1])} ${parseInt(arr[2])} / 75%)`);
        }
        if(num===7){
          setCssVar('--ant-primary-'+num,`rgb(${parseInt(arr[0])} ${parseInt(arr[1])} ${parseInt(arr[2])} / 80%)`);
        }
      }
    }

    grayMode && updateGrayMode(grayMode);
    colorWeak && updateColorWeak(colorWeak);
  } catch (error) {
    console.log(error);
  }
  appStore.setProjectConfig(projCfg);
  // 同步门户布局：优先个性化设置保存的框架，其次配置里的 layoutId
  const preferredLayout = StorageUtils.getSessionItem('xm_preferred_layout');
  const layoutId = preferredLayout || (projCfg as any)?.layoutId;
  if (layoutId) {
    StorageUtils.addItem('layout', layoutId);
    appStore.setLayout(layoutId);
    if ((projCfg as any)?.layoutId !== layoutId) {
      (projCfg as any).layoutId = layoutId;
      appStore.setProjectConfig({ layoutId } as any);
    }
  }
  // 大海深蓝 / 白色简约：按框架强制同步顶栏、菜单底色，避免刷新后仍用上一套框架的颜色
  const frameColorPreset = getFrameColorPreset(layoutId);
  const finalHeaderBg = frameColorPreset?.headerBg || headerBgColor;
  const finalMenuBg = frameColorPreset?.menuBg || bgColor;

  // init dark mode
  updateDarkTheme(darkMode);
  updatePageTheme(pageBgColor);
  if (darkMode === ThemeEnum.DARK) {
    updateHeaderBgColor();
    updateSidebarBgColor();
  } else {
    finalHeaderBg && updateHeaderBgColor(finalHeaderBg);
    finalMenuBg && updateSidebarBgColor(finalMenuBg);
  }
  // init store
  localeStore.initLocale();

  setTimeout(() => {
    clearObsoleteStorage();
  }, 16);
}

/**
 * As the version continues to iterate, there will be more and more cache keys stored in localStorage.
 * This method is used to delete useless keys
 */
export function clearObsoleteStorage() {
  const commonPrefix = getCommonStoragePrefix();
  const shortPrefix = getStorageShortName();

  [localStorage, sessionStorage].forEach((item: Storage) => {
    Object.keys(item).forEach((key) => {
      if (key && key.startsWith(commonPrefix) && !key.startsWith(shortPrefix)) {
        item.removeItem(key);
      }
    });
  });
}
